// The staff routes (/admin/*): shapes and hooks for the staff console and Mission control.
import { useQuery } from "@tanstack/react-query";
import { api } from "./client";
import type { IntakeMessage, Page, PortalUser, StaffTenant } from "./types";

export interface OpsOverview {
  health: "Healthy" | "Degraded" | "Paused" | "Stopped";
  environment: string;
  versions: { worker: string | null; api: string | null };
  replicas: { running: number; min: number; max: number };
  lastHeartbeatAt: string | null;
  queue: { active: number | null; scheduled: number | null; deadLettered: number | null; oldestWaitingSeconds: number | null };
  inFlight: number;
  lastHour: { vouched: number; review: number; failed: number };
  serviceLevel24h: { withinTargetRatio: number | null; p95Seconds: number | null; finished: number };
  spend: { todayUsd: number; monthUsd: number };
  intakeLastHour: { accepted: number; rejected: number; unknownAddress: number };
  switches: { paused: boolean; syncoreWritesStopped: boolean };
}

export interface Worker {
  instanceId: string;
  version: string | null;
  startedAt: string;
  lastHeartbeatAt: string;
  stale: boolean;
  inFlight: { invoiceId: string; tenantId: string; step: string; since: string }[];
  sessions: string[];
}

export interface QueueView {
  byTenant: { tenantId: string; state: string; queued: number; held: number; inFlight: number; oldestWaitingSeconds: number | null; ownerInstance: string | null }[];
  deadLetters: { messageId: string; invoiceId: string | null; tenantId: string | null; deliveryCount: number; reason: string | null; enqueuedAt: string }[];
}

export interface Scaling {
  replicas: { min: number; max: number; running: number; maxAllowed: number };
  trigger: { messagesPerReplica: number };
  revision: string | null;
  editable: boolean;
}

export interface Limits {
  tenantId: string;
  name: string;
  state: string;
  maxConcurrency: number;
  monthlyClaudeSpendUsd: number | null;
  spentThisMonthUsd: number;
  tier: string;
  allowance: number;
  usedThisMonth: number;
  dryRun: boolean;
  intakeEnabled: boolean;
  credentialHoldUntil: string | null;
}

export interface SwitchState { on: boolean; since: string | null; by: string | null; reason: string | null }
export type Switches = Record<string, SwitchState>;

export interface Incident {
  at: string;
  kind: string;
  tenantId: string | null;
  invoiceId: string | null;
  instanceId: string | null;
  code: string | null;
  message: string;
  by: string | null;
}

export interface Ledger {
  supported: boolean;
  latest: { id: number; takenAt: string; takenBy: { type: string; id: string | null; name: string | null } | null; digest: unknown } | null;
}

const poll = { refetchInterval: 10_000 };

export function useStaffTenants() {
  return useQuery({ queryKey: ["staff", "tenants"], queryFn: () => api<StaffTenant[]>("/admin/tenants", { noTenant: true }), ...poll });
}

export function useStaffUsers() {
  return useQuery({ queryKey: ["staff", "users"], queryFn: () => api<PortalUser[]>("/admin/users", { noTenant: true }) });
}

export function useDeliveries(status: string | undefined, page: number, pageSize = 50) {
  return useQuery({ queryKey: ["staff", "deliveries", status, page, pageSize], queryFn: () => api<Page<IntakeMessage>>("/admin/intake/deliveries", { query: { status, page, pageSize }, noTenant: true }), placeholderData: (p) => p, ...poll });
}

export function useOpsOverview() {
  return useQuery({ queryKey: ["ops", "overview"], queryFn: () => api<OpsOverview>("/admin/ops/overview", { noTenant: true }), ...poll });
}

export function useWorkers() {
  return useQuery({ queryKey: ["ops", "workers"], queryFn: () => api<Worker[]>("/admin/ops/workers", { noTenant: true }), ...poll });
}

export function useQueue() {
  return useQuery({ queryKey: ["ops", "queue"], queryFn: () => api<QueueView>("/admin/ops/queue", { noTenant: true }), ...poll });
}

export function useScaling() {
  return useQuery({ queryKey: ["ops", "scaling"], queryFn: () => api<Scaling>("/admin/ops/scaling", { noTenant: true }) });
}

export function useLimits() {
  return useQuery({ queryKey: ["ops", "limits"], queryFn: () => api<Limits[]>("/admin/ops/limits", { noTenant: true }), ...poll });
}

export function useSwitches() {
  return useQuery({ queryKey: ["ops", "switches"], queryFn: () => api<Switches>("/admin/ops/switches", { noTenant: true }), ...poll });
}

export function useIncidents(kind?: string, tenantId?: string) {
  return useQuery({ queryKey: ["ops", "incidents", kind ?? "", tenantId ?? ""], queryFn: () => api<Incident[]>("/admin/ops/incidents", { query: { kind, tenantId, take: 100 }, noTenant: true }), ...poll });
}

export function useLedger() {
  return useQuery({ queryKey: ["ops", "ledger"], queryFn: () => api<Ledger>("/admin/ops/ledger", { noTenant: true }) });
}

export const INCIDENT_KINDS: Record<string, [string, string, "bad" | "warn" | "info"]> = {
  InvoiceFailed: ["octagon-x", "Invoice stopped", "bad"],
  SyncoreError: ["plug-zap", "Syncore error", "bad"],
  CredentialRejected: ["key-round", "Sign-in rejected", "bad"],
  CredentialsRejected: ["key-round", "Sign-in rejected", "bad"],
  IntakeRejected: ["mail-x", "Mail not accepted", "warn"],
  UnknownAddress: ["mail-question", "Unknown address", "warn"],
  InstanceStale: ["heart-pulse", "Worker stale", "bad"],
  SpendCapReached: ["wallet", "Spend cap reached", "warn"],
  SwitchChanged: ["toggle-right", "Switch changed", "info"],
  TenantPaused: ["pause", "Customer paused", "info"],
  TenantResumed: ["play", "Customer resumed", "info"],
  TenantCreated: ["plus", "Customer created", "info"],
  TenantOffboarded: ["trash-2", "Customer offboarded", "info"],
  LimitsChanged: ["sliders-horizontal", "Limits changed", "info"],
  ScalingChanged: ["sliders-horizontal", "Scaling changed", "info"],
  WorkRequeued: ["refresh-cw", "Work requeued", "info"],
  LedgerVerified: ["shield-check", "Ledger verified", "info"],
  LedgerBroken: ["shield", "Ledger broken", "bad"],
};

export function incidentKind(kind: string): [string, string, "bad" | "warn" | "info"] {
  return INCIDENT_KINDS[kind] ?? ["info", kind.replace(/([a-z])([A-Z])/g, "$1 $2"), "info"];
}
